'use client';

import type { VariantType } from '../../../types';
import k2cStyles from './ActionButton.k2c.module.scss';
import styles from './ActionButton.module.scss';
import raffleStyles from './ActionButton.raffle.module.scss';

export type Props = {
  children: React.ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  disabled?: boolean;
  size?: 'xSmall' | 'small' | 'large' | 'xLarge' | 'fullWidth' | 'default';
  /** 参照: src/styles/partials/_variables.scss */
  color?: string;
  bgColor?: string;
  outlined?: boolean;
  isLoading?: boolean;
  noDisableOpacity?: boolean;
  variant?: VariantType;
  dataTestId?: string;
};

function ActionButton({
  children,
  onClick,
  type = 'button',
  disabled = false,
  size = 'default',
  color = 'black',
  bgColor = 'white',
  outlined = false,
  isLoading = false,
  noDisableOpacity = false,
  variant = 'default',
  dataTestId = '',
}: Props) {
  const isClickable = !disabled && !isLoading;

  // variantに応じてスタイルを決定
  const getVariantStyles = () => {
    switch (variant) {
      case 'raffle':
        return raffleStyles;
      case 'k2c':
        return k2cStyles;
      default:
        return styles;
    }
  };

  const currentStyles = getVariantStyles();

  // カラー別クラス名を生成
  const getColorClassName = () => {
    if (!outlined) {
      switch (color) {
        case 'primary':
          return currentStyles.primary;
        case 'blue-gradient':
          return currentStyles.blueGradient;
        default:
          return currentStyles.defaultColor;
      }
    }
    return '';
  };

  return (
    <button
      className={`${currentStyles.actionButton} ${currentStyles[size]} ${
        !isClickable && !noDisableOpacity ? currentStyles.disableOpacity : ''
      } ${getColorClassName()}`}
      type={type}
      onClick={onClick}
      style={generateStyle({ color, bgColor, outlined, variant })}
      disabled={!isClickable}
      data-testid={dataTestId}
    >
      {isLoading ? <div className={currentStyles.loader} /> : children}
    </button>
  );
}

const generateStyle = ({
  color,
  bgColor,
  outlined,
  variant,
}: Pick<Props, 'color' | 'bgColor' | 'outlined' | 'variant'>): React.CSSProperties => {
  if (outlined) {
    return {
      color: `var(--color-${color})`,
      border: `2px solid var(--color-${color})`,
      background: `var(--color-${bgColor})`,
    };
  }

  // variantに応じてスタイル生成ロジックを分岐
  if (variant === 'raffle') {
    return generateRaffleStyle(color || 'black');
  }

  if (variant === 'k2c') {
    return generateK2cStyle(color || 'black');
  }

  // デフォルトvariantのスタイル生成
  return generateDefaultStyle(color || 'black');
};

const generateDefaultStyle = (color: string): React.CSSProperties => {
  // 色に応じてグラデーション
  switch (color) {
    case 'primary':
      return {
        border: 'none',
        background: 'linear-gradient(to top, var(--color-gradationprimary), var(--color-primary))',
      };
    case 'blue-gradient':
      return {
        border: 'none',
        background: 'linear-gradient(to top, #1190e0, #33b0ff)',
      };
    default:
      return {
        border: `var(--color-${color})`,
        background: `var(--color-${color})`,
      };
  }
};

const generateRaffleStyle = (color: string): React.CSSProperties => {
  // 色に応じてグラデーション
  switch (color) {
    case 'primary':
      return {
        border: 'none',
        background: 'linear-gradient(to top, var(--color-gradationprimary), var(--color-primary))',
      };
    case 'blue-gradient':
      return {
        border: 'none',
        background: 'linear-gradient(to top, #1190e0, #33b0ff)',
      };
    default:
      return {
        border: `var(--color-${color})`,
        background: `var(--color-${color})`,
      };
  }
};

const generateK2cStyle = (color: string): React.CSSProperties => {
  // k2cは基本的にシンプルな色指定
  return {
    border: `var(--color-${color})`,
    background: `var(--color-${color})`,
  };
};

export default ActionButton;
